import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "./Icon.tsx";

export interface SelectOption {
  value: string;
  label: string;
  /** A second line or a tag after the label ("A site", "lower level"). */
  hint?: string;
  icon?: ReactNode;
  disabled?: boolean;
}
export interface SelectGroup {
  label?: string;
  options: SelectOption[];
}

/** From this many choices, the list gets a search box. */
const SEARCH_FROM = 9;

/**
 * The app's own dropdown: a button showing the choice, and a list under it in
 * groups, with a search box when it is long. Keys: ↑ ↓ to move, Enter to pick,
 * Escape to close, letters to search. Drawn above the page, so a card or a map
 * never cuts it off; it opens upwards when there is no room below.
 */
export function Select({
  label,
  value,
  placeholder,
  groups,
  onChange,
  className,
  buttonIcon,
}: {
  /** What it is for, for screen readers (and the list's name). */
  label: string;
  value: string | null;
  placeholder: string;
  groups: SelectGroup[];
  onChange: (value: string) => void;
  className?: string;
  /** Shown before the chosen label in the button. */
  buttonIcon?: ReactNode;
}) {
  const { t } = useTranslation();
  const id = useId();
  const button = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState<string | null>(null);
  const [place, setPlace] = useState<{ left: number; top: number; width: number; up: boolean; max: number } | null>(null);

  const all = groups.flatMap((g) => g.options);
  const chosen = all.find((o) => o.value === value) ?? null;
  const searchable = all.length >= SEARCH_FROM;
  const q = query.trim().toLowerCase();
  const shown = useMemo(
    () => groups.map((g) => ({ ...g, options: g.options.filter((o) => !q || o.label.toLowerCase().includes(q) || (o.hint ?? "").toLowerCase().includes(q)) })).filter((g) => g.options.length),
    [groups, q],
  );
  const flat = shown.flatMap((g) => g.options).filter((o) => !o.disabled);
  // Element ids by position: values may hold spaces.
  const optId = (v: string) => `${id}-o${all.findIndex((o) => o.value === v)}`;

  // Where the list goes: under the button, or above it when the window ends first.
  useLayoutEffect(() => {
    if (!open) return;
    const fit = () => {
      const r = button.current?.getBoundingClientRect();
      if (!r) return;
      const below = window.innerHeight - r.bottom - 12;
      const above = r.top - 12;
      const up = below < 240 && above > below;
      setPlace({ left: r.left, top: up ? r.top - 6 : r.bottom + 6, width: Math.max(r.width, 220), up, max: Math.min(360, up ? above : below) });
    };
    fit();
    window.addEventListener("resize", fit);
    window.addEventListener("scroll", fit, true);
    return () => {
      window.removeEventListener("resize", fit);
      window.removeEventListener("scroll", fit, true);
    };
  }, [open]);

  // A click elsewhere closes it.
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!list.current?.contains(target) && !button.current?.contains(target)) setOpen(false);
    };
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [open]);

  // Opened: the search box (or the list) takes the keys, the choice is in view.
  useEffect(() => {
    if (!open) return;
    (search.current ?? list.current)?.focus();
    list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
  }, [open, place]);

  const show = () => {
    setQuery("");
    setActive(value ?? flat[0]?.value ?? null);
    setOpen(true);
  };
  const close = (refocus = true) => {
    setOpen(false);
    if (refocus) button.current?.focus();
  };
  const pick = (o: SelectOption) => {
    if (o.disabled) return;
    close();
    if (o.value !== value) onChange(o.value);
  };
  const onKey = (e: KeyboardEvent) => {
    const at = flat.findIndex((o) => o.value === active);
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const next = flat[Math.max(0, Math.min(flat.length - 1, at + (e.key === "ArrowDown" ? 1 : -1)))] ?? flat[0];
      if (next) {
        setActive(next.value);
        list.current?.querySelector(`[data-value="${CSS.escape(next.value)}"]`)?.scrollIntoView({ block: "nearest" });
      }
    } else if (e.key === "Enter") {
      e.preventDefault();
      const o = flat[at] ?? flat[0];
      if (o) pick(o);
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === "Tab") close(false);
  };

  return (
    <>
      <button
        ref={button}
        type="button"
        className={`select-button${chosen ? "" : " is-empty"}${className ? ` ${className}` : ""}`}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? `${id}-list` : undefined}
        aria-label={label}
        onClick={() => (open ? close() : show())}
        onKeyDown={(e) => {
          if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
            e.preventDefault();
            show();
          }
        }}
      >
        {chosen?.icon ?? buttonIcon}
        <span className="select-value">{chosen ? chosen.label : placeholder}</span>
        {chosen?.hint && <span className="select-hint">{chosen.hint}</span>}
        <Icon name="chevronDown" size={14} />
      </button>
      {open &&
        place &&
        createPortal(
          <div
            ref={list}
            className={`select-pop${place.up ? " is-up" : ""}`}
            style={{ left: place.left, top: place.top, width: place.width, maxHeight: place.max }}
            tabIndex={-1}
            onKeyDown={onKey}
          >
            {searchable && (
              <div className="select-search">
                <Icon name="search" size={14} />
                <input
                  ref={search}
                  value={query}
                  placeholder={t("select.search")}
                  aria-label={t("select.searchIn", { what: label })}
                  aria-controls={`${id}-list`}
                  aria-activedescendant={active ? optId(active) : undefined}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setActive(null);
                  }}
                />
              </div>
            )}
            <div id={`${id}-list`} className="select-list" role="listbox" aria-label={label} aria-activedescendant={active ? optId(active) : undefined}>
              {shown.map((g, gi) => (
                <div key={gi} role="group" aria-label={g.label} className="select-group">
                  {g.label && (
                    <div className="select-group-label" aria-hidden="true">
                      {g.label}
                    </div>
                  )}
                  {g.options.map((o) => (
                    <div
                      key={o.value}
                      id={optId(o.value)}
                      data-value={o.value}
                      role="option"
                      aria-selected={o.value === value}
                      aria-disabled={o.disabled || undefined}
                      className={`select-option${o.value === active ? " is-active" : ""}${o.disabled ? " is-disabled" : ""}`}
                      onPointerEnter={() => !o.disabled && setActive(o.value)}
                      onClick={() => pick(o)}
                    >
                      {o.icon}
                      <span className="select-option-label">{o.label}</span>
                      {o.hint && <span className="select-hint">{o.hint}</span>}
                      {o.value === value && <Icon name="check" size={14} />}
                    </div>
                  ))}
                </div>
              ))}
              {shown.length === 0 && <p className="select-none small muted">{t("select.none")}</p>}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
